import { Image, Popover } from 'antd';
import { useState } from 'react';
import type { MessageImage } from '@entities/chat';
import { env } from '@shared/config/env';
import './message-images.css';

interface Props {
  images: MessageImage[];
}

/**
 * Картинка идёт через наш прокси /api/images/{id}/raw: он читает файл из хранилища
 * по ключу, поэтому открывается и при закрытом бакете, и без настроенного CDN.
 */
export function imageSrc(img: MessageImage): string {
  return `${env.API_BASE_URL.replace(/\/$/, '')}/images/${img.id}/raw`;
}

/**
 * Скриншоты под ответом ассистента.
 *  - наведение — увеличенная картинка во всплывающем окне (с подписью);
 *  - клик — полноэкранный просмотр с листанием и зумом (AntD PreviewGroup);
 *  - картинка не загрузилась — прячем её, без «битой» иконки и длинного alt.
 */
export function MessageImages({ images }: Props) {
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const shown = images.filter((img) => img.id && !failed.has(img.id));
  if (shown.length === 0) return null;

  const fail = (id: string) =>
    setFailed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  return (
    <div className="mi-wrap">
      <Image.PreviewGroup>
        {shown.map((img) => {
          const src = imageSrc(img);
          const caption = img.caption ?? 'Иллюстрация из документа';
          return (
            <Popover
              key={img.id}
              placement="top"
              mouseEnterDelay={0.15}
              arrow={false}
              overlayClassName="mi-pop"
              content={
                <figure className="mi-zoom">
                  <img src={src} alt={caption} />
                  {img.caption && <figcaption>{img.caption}</figcaption>}
                </figure>
              }
            >
              <div className="mi-thumb">
                <Image
                  src={src}
                  alt={caption}
                  width={152}
                  height={95}
                  onError={() => fail(img.id)}
                  preview={{ mask: false }}
                />
                {img.page_number != null && <span className="mi-page">стр. {img.page_number}</span>}
              </div>
            </Popover>
          );
        })}
      </Image.PreviewGroup>
    </div>
  );
}
